import React, { useEffect, useState } from 'react';
import styles from './procedimentos_isabellacoimbra_sobrancelhas.module.scss';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const WHATSAPP_NUMBER = '5517988130264';

type Procedimento = {
    id: string;
    titulo: string;
    descricao: string[];
    destaques?: string[];
    precos: { label?: string; valor: string }[];
    imagens: string[];
};

const procedimentos: Procedimento[] = [
    {
        id: 'brow-lamination',
        titulo: 'Brow Lamination',
        descricao: [
            'O Brow Lamination é um procedimento perfeito para quem deseja sobrancelhas mais alinhadas, preenchidas e com aquele efeito lindo de fios penteados.',
            'A técnica trabalha os próprios fios da sobrancelha, deixando-os mais maleáveis e organizados, o que ajuda a criar a sensação de uma sobrancelha mais volumosa e definida.',
            'É ideal para quem tem fios rebeldes, que crescem em direções diferentes, ou simplesmente ama aquele efeito mais moderno e sofisticado nas sobrancelhas.',
        ],
        precos: [
            { label: 'Brow Lamination', valor: 'R$ 150,00' },
            { label: 'Brow Lamination com coloração', valor: 'R$ 170,00' },
        ],
        imagens: [
            require('./src/procedimentos/brow1.png'),
            require('./src/procedimentos/brow2.png'),
        ],
    },
    {
        id: 'micropigmentacao-labial',
        titulo: 'Micropigmentação Labial',
        descricao: [
            'Trazendo mais saúde, cor e vitalidade aos seus lábios, com um delicado efeito Lip Tint.',
        ],
        precos: [{ valor: 'R$ 550,00' }],
        imagens: [
            require('./src/procedimentos/microlabial1.png'),
            require('./src/procedimentos/microlabial2.png'),
        ],
    },
    {
        id: 'design-personalizado',
        titulo: 'Design Personalizado, Estruturado e Restaurador de Fios',
        descricao: [],
        precos: [{ valor: 'R$ 40,00' }],
        imagens: [require('./src/procedimentos/designerpersonalizado1.png')],
    },
    {
        id: 'design-henna',
        titulo: 'Design Personalizado com Henna',
        descricao: [
            'Formato desenvolvido especialmente para valorizar os traços e a beleza de cada cliente, respeitando o formato natural das sobrancelhas.',
        ],
        precos: [{ valor: 'R$ 65,00' }],
        imagens: [
            require('./src/procedimentos/henna1.png'),
            require('./src/procedimentos/henna2.png'),
        ],
    },
    {
        id: 'micropigmentacao-sobrancelhas',
        titulo: 'Micropigmentação de Sobrancelhas',
        descricao: [
            'Sobrancelhas mais definidas, preenchidas e harmonizadas com o seu rosto, respeitando seus traços e sua beleza natural.',
        ],
        destaques: [
            'Técnica personalizada',
            'Design pensado para o seu formato de rosto',
            'Mais praticidade no dia a dia',
        ],
        precos: [{ valor: 'R$ 650,00' }],
        imagens: [require('./src/procedimentos/micropigmentacaosobrancelha1.png')],
    },
];

interface CardProcedimentoProps {
    item: Procedimento;
}

const CardProcedimento: React.FC<CardProcedimentoProps> = ({ item }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [expandido, setExpandido] = useState(false);
    const hasMultipleImages = item.imagens.length > 1;
    const hasMoreText = item.descricao.length > 1;
    const paragrafosVisiveis = expandido || !hasMoreText ? item.descricao : item.descricao.slice(0, 1);

    const prevImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!hasMultipleImages) return;
        setCurrentIndex((prev) => (prev === 0 ? item.imagens.length - 1 : prev - 1));
    };

    const nextImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!hasMultipleImages) return;
        setCurrentIndex((prev) => (prev === item.imagens.length - 1 ? 0 : prev + 1));
    };

    const handleAgendamentoClick = () => {
        const msg = encodeURIComponent(
            `Olá! Vim pelo cartão digital e gostaria de agendar o procedimento: ${item.titulo}.`
        );
        window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, '_blank', 'noopener,noreferrer');
    };

    return (
        <section className={styles.procedimento}>
            <div className={styles.carousel}>
                <img src={item.imagens[currentIndex]} alt={item.titulo} />
                {hasMultipleImages && (
                    <>
                        <button
                            type="button"
                            className={`${styles.carouselBtn} ${styles.prevBtn}`}
                            onClick={prevImage}
                            aria-label="Imagem anterior"
                        >
                            <FaChevronLeft />
                        </button>
                        <button
                            type="button"
                            className={`${styles.carouselBtn} ${styles.nextBtn}`}
                            onClick={nextImage}
                            aria-label="Próxima imagem"
                        >
                            <FaChevronRight />
                        </button>
                        <div className={styles.indicators}>
                            {item.imagens.map((_, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    className={`${styles.dot} ${idx === currentIndex ? styles.active : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCurrentIndex(idx);
                                    }}
                                    aria-label={`Ir para imagem ${idx + 1}`}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            <h3 className={styles.titulo}>{item.titulo}</h3>

            {paragrafosVisiveis.map((texto) => (
                <p key={texto} className={styles.descricao}>
                    {texto}
                </p>
            ))}

            {hasMoreText && (
                <button
                    type="button"
                    className={styles.verMais}
                    onClick={() => setExpandido((prev) => !prev)}
                >
                    {expandido ? 'Ver menos' : 'Ver mais'}
                </button>
            )}

            {item.destaques && item.destaques.length > 0 && (
                <ul className={styles.destaques}>
                    {item.destaques.map((destaque) => (
                        <li key={destaque}>{destaque}</li>
                    ))}
                </ul>
            )}

            <div className={styles.precos}>
                {item.precos.map((preco) => (
                    <div key={`${preco.label ?? item.id}-${preco.valor}`} className={styles.precoItem}>
                        {preco.label ? <span className={styles.precoLabel}>{preco.label}</span> : null}
                        <span className={styles.precoValor}>{preco.valor}</span>
                    </div>
                ))}
            </div>

            <button type="button" className={styles.agendarBtn} onClick={handleAgendamentoClick}>
                Agendar
            </button>
        </section>
    );
};

const Procedimentos_isabellacoimbra_sobrancelhas: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.procedimentos_isabellacoimbra_sobrancelhas}>
                <div className={styles.header}>
                    <Link to="/isabellacoimbra_sobrancelhas" className={styles.backButton} aria-label="Voltar">
                        <FaArrowLeft />
                    </Link>
                    <h2>Procedimentos</h2>
                </div>

                <p className={styles.intro}>
                    Conheça os procedimentos e escolha o melhor para valorizar o seu olhar e a sua beleza natural.
                </p>

                <div className={styles.lista}>
                    {procedimentos.map((item) => (
                        <CardProcedimento key={item.id} item={item} />
                    ))}
                </div>

                <section className={styles.ctaSection}>
                    <p className={styles.ctaTitle}>Pronta para agendar?</p>
                    <p className={styles.ctaText}>
                        Clique em Agendar no procedimento desejado e escolha o melhor horário para você.
                    </p>
                </section>
            </div>
        </div>
    );
};

export default Procedimentos_isabellacoimbra_sobrancelhas;
